import React, { useState, useEffect, useMemo } from "react";
import { 
    Box, Typography, MenuItem, FormControl, Select, 
    Avatar, CircularProgress, Divider, TextField,
    InputAdornment, useTheme, alpha, Skeleton
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import BusinessIcon from "@mui/icons-material/Business";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "../utils/toast";
import { setActiveCurrency } from "../utils/currency";

/**
 * PropertySwitcher - A premium UI component for switching between different hotel properties (tenants).
 * Data is fetched from the tenant API (GET /auth/tenants) on mount.
 */
export default function PropertySwitcher() {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { user, tenants, isLoading } = useSelector((state) => state.auth);
    const [switching, setSwitching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (tenants.length === 0) {
            dispatch.auth.fetchTenantsAsync().then((res) => {
                if (!res?.success) {
                    toast.error("Could not load property list. Please try again.");
                }
            });
        }
    }, [dispatch, tenants.length]);

    const handleSwitch = async (event) => {
        const propertyUuid = event.target.value;

        if (propertyUuid === "all_properties") {
            if (!user?.tenantId || user?.tenantId === 'null') return;
            setSwitching(true);
            try {
                // Prepare branding for global view without flashing old/default branding
                if (dispatch.branding?.prepareTenantSwitch) {
                    dispatch.branding.prepareTenantSwitch(null);
                } else if (dispatch.branding) {
                    dispatch.branding.resetBranding();
                }
                // Frontend-only: restore the global token saved at login time
                // No API call is needed — we simply discard the tenant-scoped token
                const result = dispatch.auth.restoreGlobalSession();
                if (result.success) {
                    window.location.reload();
                }
            } catch (error) {
                console.error("Failed to restore global session:", error);
                toast.error("Failed to switch to global view.");
            } finally {
                setSwitching(false);
            }
            return;
        }

        const selectedTenant = tenants.find(t => (t.id || t._id) === propertyUuid);
        const stringTenantId = selectedTenant?.tenantId;

        if (stringTenantId === String(user?.tenantId) || switching) return;

        setSwitching(true);
        try {
            // Prepare branding for the new tenant immediately to prevent old tenant bleed or green flicker
            if (dispatch.branding?.prepareTenantSwitch) {
                dispatch.branding.prepareTenantSwitch(stringTenantId || propertyUuid);
            } else if (dispatch.branding) {
                dispatch.branding.resetBranding();
            }
            if (selectedTenant?.currency) {
                setActiveCurrency(selectedTenant.currency);
            }
            const result = await dispatch.auth.switchTenantAsync({
                payloadTenantId: propertyUuid,
                stringTenantId: stringTenantId || propertyUuid
            });
            if (result?.success) {
                // Success - reload the page to refresh all data for the new tenant
                window.location.reload();
            } else {
                toast.error(result?.message || "Failed to switch property.");
            }
        } catch (error) {
            console.error("Failed to switch property:", error);
            toast.error("Failed to switch property. Please try again.");
        } finally {
            setSwitching(false);
        }
    };

    const handleClose = () => {
        setSearchQuery("");
    };

    const normalizeRole = (role) => {
        const roleName = typeof role === "object" && role !== null ? role.name : role;
        return String(roleName || "").replace(/[_\s-]+/g, "").toUpperCase();
    };

    const isSuperAdmin = useMemo(() => {
        return normalizeRole(user?.role) === "SUPERADMIN";
    }, [user]);

    const filteredTenants = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return tenants;
        return tenants.filter((t) => {
            const name = (t.name || t.hotelName || "").toLowerCase();
            const id = String(t.id || t._id || "").toLowerCase();
            return name.includes(query) || id.includes(query);
        });
    }, [tenants, searchQuery]);

    // Skeleton loading state
    if (isLoading && tenants.length === 0) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="rounded" width={140} height={32} sx={{ borderRadius: 3 }} />
            </Box>
        );
    }

    if (tenants.length === 0 && !isLoading) return null;

    const selectValue = (!user?.tenantId || user?.tenantId === 'null')
        ? "all_properties"
        : (tenants.find(t => String(t.tenantId || t.id || t._id) === String(user?.tenantId))?.id || user?.tenantId || "");

    return (
        <FormControl 
            size="small" 
            sx={{ 
                minWidth: 210,
                "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    transition: 'all 0.15s ease-in-out',
                    "&:hover": {
                        bgcolor: "#e2e8f0",
                        borderColor: "#cbd5e1",
                    },
                    "& fieldset": { border: 'none' },
                    "&.Mui-focused": {
                        borderColor: theme.palette.primary.main,
                        bgcolor: "#ffffff",
                    },
                }
            }}
        >
            <Select
                value={selectValue}
                onChange={handleSwitch}
                onClose={handleClose}
                displayEmpty
                disabled={switching}
                IconComponent={() => (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        {switching ? (
                            <CircularProgress size={16} thickness={5} />
                        ) : (
                            <UnfoldMoreRoundedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                        )}
                    </Box>
                )}
                renderValue={(selected) => {
                    if (selected === "all_properties") {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar 
                                    sx={{ 
                                        width: 24, 
                                        height: 24, 
                                        borderRadius: "6px",
                                        bgcolor: theme.palette.primary.main, 
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        color: '#ffffff',
                                    }}
                                >
                                    <BusinessIcon sx={{ fontSize: 15 }} />
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }} noWrap>
                                    All Properties
                                </Typography>
                            </Box>
                        );
                    }
                    const currentTenant = tenants.find(t => (t.id || t._id) === selected || t.tenantId === selected);
                    const hotelName = currentTenant?.name || currentTenant?.hotelName || "Switch Property";
                    const initial = hotelName.charAt(0).toUpperCase();

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Avatar 
                                sx={{ 
                                    width: 24, 
                                    height: 24, 
                                    borderRadius: "6px",
                                    bgcolor: theme.palette.primary.main, 
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    color: '#ffffff',
                                }}
                            >
                                {initial}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }} noWrap>
                                {hotelName}
                            </Typography>
                        </Box>
                    );
                }}
                sx={{
                    "& .MuiSelect-select": { 
                        display: 'flex', 
                        alignItems: 'center', 
                        py: 0.65,
                        px: 1.25,
                        pr: '30px !important'
                    }
                }}
                MenuProps={{
                    autoFocus: false,
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="overline" color="text.secondary" fontWeight="bold">
                        Switch Property
                    </Typography>
                </Box>
                
                {/* Search input field */}
                <Box 
                    sx={{ px: 2, pb: 1.5 }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <TextField
                        size="small"
                        placeholder="Search properties..."
                        fullWidth
                        autoFocus
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 2 }
                        }}
                    />
                </Box>
                
                <Divider />

                {isSuperAdmin && (
                    <MenuItem 
                        value="all_properties"
                        sx={{ 
                            py: 1.5,
                            px: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 4,
                            borderRadius: 1.5,
                            m: 0.5,
                            "&.Mui-selected": {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar 
                                sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    bgcolor: !user?.tenantId || user?.tenantId === 'null' ? 'primary.main' : 'grey.200',
                                    color: !user?.tenantId || user?.tenantId === 'null' ? 'white' : 'text.secondary'
                                }}
                            >
                                <BusinessIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="body2" fontWeight={!user?.tenantId || user?.tenantId === 'null' ? "bold" : "medium"}>
                                    All Properties (Super Admin)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    No tenant filter applied
                                </Typography>
                            </Box>
                        </Box>
                        {(!user?.tenantId || user?.tenantId === 'null') && <CheckCircleIcon color="primary" sx={{ fontSize: 18 }} />}
                    </MenuItem>
                )}
                {isSuperAdmin && <Divider />}

                {filteredTenants.length > 0 ? (
                    filteredTenants.map((t) => {
                        const propertyUuid = t.id || t._id;
                        const isActive = String(t.tenantId || propertyUuid) === String(user?.tenantId);
                        return (
                            <MenuItem 
                                key={propertyUuid} 
                                value={propertyUuid}
                                sx={{ 
                                    py: 1.5,
                                    px: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 4,
                                    borderRadius: 1.5,
                                    m: 0.5,
                                    "&.Mui-selected": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar 
                                        sx={{ 
                                            width: 32, 
                                            height: 32, 
                                            bgcolor: isActive ? 'primary.main' : 'grey.200',
                                            color: isActive ? 'white' : 'text.secondary'
                                        }}
                                    >
                                        {(t.name || t.hotelName || "P").charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight={isActive ? "bold" : "medium"}>
                                            {t.name || t.hotelName || `Property ${propertyUuid}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Tenant: {t.tenantId || String(propertyUuid).slice(0, 8) + '…'}
                                        </Typography>
                                    </Box>
                                </Box>
                                {isActive && <CheckCircleIcon color="primary" sx={{ fontSize: 18 }} />}
                            </MenuItem>
                        );
                    })
                ) : (
                    <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No properties found
                        </Typography>
                    </Box>
                )}
            </Select>
        </FormControl>
    );
}
